import { axiosInstance } from '../App'
import { SECURED_API_BASE } from './apiConfig'
import {
  StockItemTypeContractListItemDTO,
  StockItemTypeContractListQueryParams,
  StockItemTypeContractOptionDTO,
  StockItemTypeContractOptionsDTO,
  StockItemTypeContractCreateDTO,
  StockItemTypeContractCreateResultDTO,
  StockItemTypeContractPlanOptionDTO,
  StockItemTypeContractUpdateDTO,
} from '../dto/stockItemTypeContract/StockItemTypeContractDTO'

const BASE = `${SECURED_API_BASE}/stock-item-type-contracts`

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** Las fechas llegan como yyyy-MM-dd; se acepta también [yyyy, M, d] por si cambia la serialización. */
function toIsoDate(value: unknown): string | null {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value.map(Number)
    if ([year, month, day].every(Number.isFinite)) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }
  }
  return null
}

function normalizeListItem(raw: unknown): StockItemTypeContractListItemDTO | null {
  if (!isRecord(raw)) return null
  const id = toNumberOrNull(raw.stockitemtypecontract)
  const healthEntity = toNumberOrNull(raw.healthentity)
  const percentage = toNumberOrNull(raw.percentage)
  const validFrom = toIsoDate(raw.validfrom)
  if (id === null || healthEntity === null || percentage === null || validFrom === null) return null

  return {
    stockitemtypecontract: id,
    healthentity: healthEntity,
    healthentityname: toText(raw.healthentityname),
    healthentityplan: toNumberOrNull(raw.healthentityplan),
    healthentityplanname: toText(raw.healthentityplanname),
    stockitemtype: toNumberOrNull(raw.stockitemtype),
    stockitemtypedesc: toText(raw.stockitemtypedesc),
    contractattentionmodality: toNumberOrNull(raw.contractattentionmodality),
    contractattentionmodalitydesc: toText(raw.contractattentionmodalitydesc),
    percentage,
    validfrom: validFrom,
    validuntil: toIsoDate(raw.validuntil),
  }
}

function normalizeOption(raw: unknown): StockItemTypeContractOptionDTO | null {
  if (!isRecord(raw)) return null
  const id = toNumberOrNull(raw.id)
  if (id === null) return null
  return { id, name: toText(raw.name) }
}

function normalizeOptions(raw: unknown): StockItemTypeContractOptionDTO[] {
  return Array.isArray(raw)
    ? raw.map(normalizeOption).filter((option): option is StockItemTypeContractOptionDTO => option !== null)
    : []
}

/**
 * El backend descarta los caracteres que no son ASCII de los valores de filtro y compara sin
 * tildes: se mandan sin tildes ("Unión" → "Union") y sin "%", que el backend no admite.
 */
function normalizeFilterValue(value: unknown): unknown {
  if (typeof value !== 'string') return value
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/%/g, '')
}

function normalizeFilterDTO(filterDTO: unknown): unknown {
  if (!isRecord(filterDTO) || !Array.isArray(filterDTO.filters)) return null
  return {
    ...filterDTO,
    filterLists: [],
    filters: filterDTO.filters.filter(isRecord).map((filter) => ({
      ...filter,
      values: Array.isArray(filter.values) ? filter.values.map(normalizeFilterValue) : filter.values,
    })),
  }
}

export async function getStockItemTypeContracts(
  offSet: number,
  pageSize: number,
  params: StockItemTypeContractListQueryParams = {},
): Promise<StockItemTypeContractListItemDTO[]> {
  const response = await axiosInstance.post(`${BASE}/list`, {
    loadConfigDTO: {
      offSet,
      pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters: normalizeFilterDTO(params.filterDTO),
    },
  })
  const rows: unknown[] = Array.isArray(response.data) ? response.data : []
  return rows
    .map(normalizeListItem)
    .filter((item): item is StockItemTypeContractListItemDTO => item !== null)
}

export async function getStockItemTypeContractOptions(): Promise<StockItemTypeContractOptionsDTO> {
  const response = await axiosInstance.get(`${BASE}/options`)
  const data: unknown = response.data
  return {
    stockItemTypes: normalizeOptions(isRecord(data) ? data.stockItemTypes : null),
    modalities: normalizeOptions(isRecord(data) ? data.modalities : null),
  }
}

export async function getStockItemTypeContractHealthEntities(): Promise<StockItemTypeContractOptionDTO[]> {
  const response = await axiosInstance.get(`${BASE}/health-entities`)
  return normalizeOptions(response.data)
}

function normalizePlan(raw: unknown): StockItemTypeContractPlanOptionDTO | null {
  if (!isRecord(raw)) return null
  const id = toNumberOrNull(raw.id)
  const healthEntityId = toNumberOrNull(raw.healthEntityId)
  if (id === null || healthEntityId === null) return null
  return { id, name: toText(raw.name), healthEntityId, healthEntityName: toText(raw.healthEntityName) }
}

/** Planes de las obras sociales elegidas, cada uno con su obra social. */
export async function getStockItemTypeContractPlans(healthEntityIds: number[]): Promise<StockItemTypeContractPlanOptionDTO[]> {
  // axios serializa los arrays como healthEntityIds[]=1: el backend espera una lista separada por comas.
  const response = await axiosInstance.get(`${BASE}/plans`, { params: { healthEntityIds: healthEntityIds.join(',') } })
  const rows: unknown[] = Array.isArray(response.data) ? response.data : []
  return rows.map(normalizePlan).filter((plan): plan is StockItemTypeContractPlanOptionDTO => plan !== null)
}

/** Crea un contrato por cada combinación de la selección. */
export async function createStockItemTypeContracts(
  payload: StockItemTypeContractCreateDTO,
): Promise<StockItemTypeContractCreateResultDTO> {
  const response = await axiosInstance.post(BASE, payload)
  const data: unknown = response.data
  return {
    created: isRecord(data) ? (toNumberOrNull(data.created) ?? 0) : 0,
  }
}

export async function updateStockItemTypeContract(id: number, payload: StockItemTypeContractUpdateDTO): Promise<void> {
  await axiosInstance.put(`${BASE}/${id}`, payload)
}

export async function deactivateStockItemTypeContract(id: number): Promise<void> {
  await axiosInstance.post(`${BASE}/${id}/deactivate`)
}

/**
 * Contratos existentes que chocan, cuando la API responde con solapamiento de vigencias
 * (STOCK_ITEM_TYPE_CONTRACT_OVERLAP). El cuerpo es el ErrorResponse estándar más `conflicts`.
 */
export function parseOverlapConflicts(apiError: unknown): StockItemTypeContractListItemDTO[] {
  if (!isRecord(apiError) || !Array.isArray(apiError.conflicts)) return []
  return apiError.conflicts
    .map(normalizeListItem)
    .filter((item): item is StockItemTypeContractListItemDTO => item !== null)
}
